// True game-style radial menu: options sit on a circular disc centered on
// the clicked NPC; one hotspot label follows the cursor around the ring.
// The disc is clamped inside the viewport by the host, which centers it on
// the person and slides the camera over first.
import { useState } from 'react';

export interface PushCandidate {
  id: string;
  name: string;
  status: string; // '' | 'talking' | 'asleep'
}

const R_OUTER = 118; // disc radius (CSS px) — widened for airy spacing
// Exposed for the host to clamp placement.
export const R_OUTER_SQUARE = R_OUTER * 2;
const HUB = 34; // center avatar chip radius

// Warm, Tavern-UI glyph set matching the header gradients.
const GLYPHS: Record<string, string> = {
  bio: '📖',
  push: '💬',
  clear: '↺',
  save: '★',
  close: '⏻',
};

export default function RadialMenu(props: {
  name: string;
  x: number; // disc center in stage-ish client coords (RadialHost clamps viewport-side)
  y: number;
  saved: boolean;
  pushTargetName: string | null; // active push, if any
  candidates: PushCandidate[];
  onBio: () => void;
  onPush: (targetId: string | null) => void;
  onSave: () => void;
  onDelete: () => void;
  onClose: () => void;
}): JSX.Element {
  const [pushing, setPushing] = useState(false);

  // Benchmark pushed submenu needs up to 19 slices; main menu is fixed 6.
  const items: Array<{ key: string; label: string; hint: string; danger?: boolean; submenu?: boolean; onClick: () => void }> = pushing
    ? props.candidates.map((c) => ({
        key: c.id,
        label: c.name,
        hint: c.status ? `currently ${c.status}` : 'free to talk',
        onClick: () => props.onPush(c.id),
      }))
    : [
        { key: 'bio', label: 'View Bio', hint: 'full dossier' , onClick: props.onBio },
        {
          key: 'push',
          label: props.pushTargetName ? `Push talk… (→ ${props.pushTargetName})` : 'Push talk to…',
          hint: 'direct their interest for 2 min',
          submenu: true,
          onClick: () => setPushing(true),
        },
        { key: 'clear', label: 'Clear push', hint: 'cancel the active push', onClick: () => props.onPush(null) },
        { key: 'save', label: props.saved ? '★ Saved' : 'Save', hint: 'keep for later', onClick: props.onSave },
        { key: 'delete', label: 'Delete', hint: 'remove from the world', danger: true, onClick: props.onDelete },
        { key: 'close', label: 'Close', hint: 'esc also works', onClick: props.onClose },
      ];

  const n = items.length;
  const slice = (Math.PI * 2) / n;
  // Start at 12 o'clock, distribute clockwise. Wider radius + item-gap so
  // neighbouring medallions never crowd.
  const pos = (i: number): { x: number; y: number } => {
    const ang = -Math.PI / 2 + slice * i + slice / 2;
    const r = HUB + (R_OUTER - HUB) / 2 + 14;
    return { x: Math.cos(ang) * r, y: Math.sin(ang) * r };
  };

  return (
    <div
      className="radial-disc"
      style={{ left: props.x, top: props.y, width: R_OUTER * 2, height: R_OUTER * 2 }}
    >
      {/* Ring slices */}
      {items.map((it, i) => {
        const p = pos(i);
        return (
          <button
            key={it.key}
            className="radial-wedge"
            style={{ left: R_OUTER + p.x - 32, top: R_OUTER + p.y - 32 }}
            title={it.hint}
            onClick={it.onClick}
          >
            <span className={it.danger ? 'wedge-glyph danger' : 'wedge-glyph'}>{it.danger ? '✕' : it.submenu ? '»' : GLYPHS[it.key] ?? '◦'}</span>
            <span className="wedge-label">{it.label}</span>
          </button>
        );
      })}
      {/* Center: name plate only (avatar chip removed) */}
      <div className="radial-core">
        <div className="radial-name">{props.name}</div>
        {pushing && <div className="radial-back-button" onClick={() => setPushing(false)}>← back</div>}
      </div>
    </div>
  );
}
